import { registerAddFileSubmittedAction } from '@/company-portal/add-file/add-file-reset';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { handleClientInfoErrors } from '@/company-portal/add-file/client-info-form-errors';
import { createEmptyErrors } from '@/components/Views/Company/File/Client/client-info-form.validation';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { v4 as uuidV4 } from 'uuid';
import { onUnmounted, watch } from 'vue';
import type { AddFileClient, AddFileClientKey, ClientInfoFormRowIndex } from './add-file.types';

const EMPTY_CLIENT: Omit<AddFileClient, 'id'> = {
    email: '',
    phone: '',
    role: undefined,
    first: '',
    last: '',
};

export type ClientInfoFormOptions = {
    minVisibleClientInfoForms?: number;
    hiddenClientInfoFormsCount?: number;
};

/**
 * legacy form submission => use form row index (e.g. '0', '1', etc.)
 * new form submission => use UUID
 * @todo [CLENG-4993] cleanup when new_add_file_submission_enabled FF is decommissioned
 * */
export const createAddFileClientKey = (formRowIndex: ClientInfoFormRowIndex): AddFileClientKey => {
    const featureFlagsStore = useFeatureFlagsStore();
    return featureFlagsStore.flags?.new_add_file_submission_enabled ? uuidV4() : formRowIndex;
};

export const useClientInfoForms = ({
    minVisibleClientInfoForms = 0,
    hiddenClientInfoFormsCount = 0,
}: ClientInfoFormOptions = {}) => {
    const fileClientStore = useFileClientStore();
    const addFileStore = useAddFileStore();

    // map dotted client-row errors (email.0, phone.1, role.2) onto each card; reset rows first
    watch(
        () => addFileStore.submissionErrors,
        response => {
            fileClientStore.clientInfoErrors.forEach((_errors, key) =>
                fileClientStore.clientInfoErrors.set(key, createEmptyErrors()),
            );
            if (response?.errors) {
                handleClientInfoErrors(response.errors, fileClientStore.clientInfoErrors);
            }
        },
    );

    // starts at 0 unless there are hidden clients from TPS
    // does not decrease when a row is removed
    let clientInfoFormIndex = hiddenClientInfoFormsCount;

    const getNextClientInfoFormIndex = () => `${clientInfoFormIndex++}`;

    const addClient = (client?: Partial<AddFileClient>): AddFileClientKey => {
        const clientKey = createAddFileClientKey(getNextClientInfoFormIndex());
        fileClientStore.userAddedClients.set(clientKey, {
            ...EMPTY_CLIENT,
            ...client,
            id: clientKey,
        });
        fileClientStore.clientInfoErrors.set(clientKey, createEmptyErrors());
        return clientKey;
    };

    const removeClient = (key: AddFileClientKey) => {
        fileClientStore.userAddedClients.delete(key);
        fileClientStore.clientInfoErrors.delete(key);
    };

    // reset state after form submission
    const resetClientInfoForms = () => {
        clientInfoFormIndex = hiddenClientInfoFormsCount;
        fileClientStore.userAddedClients.clear();

        while (fileClientStore.userAddedClients.size < minVisibleClientInfoForms) {
            addClient();
        }
    };

    resetClientInfoForms();

    const unregisterAddFileSubmittedAction = registerAddFileSubmittedAction(resetClientInfoForms);

    onUnmounted(unregisterAddFileSubmittedAction);

    return {
        addClient,
        removeClient,
        resetClientInfoForms,
    };
};
